import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useDialogSearchParam } from "@/hooks/use-dialog-search-param";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import {
  TicketCreateModal,
  type TicketFormData,
} from "@/components/ticket-create-modal";
import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InteractiveCard } from "@/components/ui/interactive-card";
import { CustomSelect } from "@/components/ui/custom-select";
import { EmptyState, ErrorState, Text } from "@/components/ui/text";
import { Fab } from "@/components/ui/fab";
import { FilterChip } from "@/components/ui/filter-chip";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import { PageTitle } from "@/contexts/page-title-context";
import {
  SortableHeaderCell,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { ResizeHandle } from "@/components/ui/resize-handle";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import {
  ResizableTableShell,
  type ColumnDef,
} from "@/components/ui/resizable-table-shell";
import { HStack } from "@/components/ui/h-stack";
import { TableViewport } from "@/components/ui/table-viewport";
import { VStack } from "@/components/ui/v-stack";
import { DividedList } from "@/components/ui/divided-list";
import { StatusDot } from "@/components/ui/status-dot";
import { useSizeClass } from "@/hooks/use-size-class";
import { useSortableTable } from "@/hooks/use-sortable-table";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { apiClient } from "@/lib/api/client";
import { formatDate } from "@/lib/formatting";
import { fromIntlLanguage } from "@/lib/i18n";
import { useUserEntity } from "@/contexts/user-entity-context";
import { getCurrentUser } from "@/lib/api/auth";
import type { PaginatedResponse } from "@/lib/api/types";
import { ticketPriorityColor, ticketStatusColor } from "@/lib/status-config";
import type { TicketStatus, TicketPriority, TicketType } from "@/lib/api/enums";
import {
  type Ticket,
  TICKET_STATUSES,
  TICKET_PRIORITIES,
} from "@/lib/api/tickets";
import { SLABadge } from "@/components/shared/sla-badge";
import { ReasonBadge } from "@/components/shared/reason-badge";
import { ActionQueueCards } from "@/components/dashboard";
import {
  getTicketPriorityLabel,
  getTicketStatusLabel,
  getTicketTypeLabel,
} from "@/lib/ticket-labels";
import { highlight } from "@/lib/highlight";
import { ticketSearchableFields } from "@/lib/search/ticket";

const TICKET_TYPES: readonly TicketType[] = [
  "ENTITY_VERIFICATION_REVIEW",
  "LOAD_APPROVAL_REVIEW",
  "BID_REVIEW",
  "BID_NEGOTIATION",
  "PICKUP_CONFIRMATION",
  "UNLOAD_CONFIRMATION",
  "ISSUE_RESOLUTION",
  "ESCALATION",
  "SUPPORT_REQUEST",
] as const;

type StatusFilter = TicketStatus | "All";
type PriorityFilter = TicketPriority | "All";
type TypeFilter = TicketType | "All";
type OwnerFilter = "All" | "Mine" | "Unassigned";
function TicketsPage() {
  const navigate = useNavigate();

  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tCommon } = useTranslation("common");
  const { t: tTickets } = useTranslation("tickets");
  const { t: tNotificationsEmpty } = useTranslation("notifications.empty");
  const { t: tTicketTypes } = useTranslation("dashboard.workTable.ticketTypes");
  const { entity } = useUserEntity();
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const isRegular = sizeClass === "regular";
  const { dialog, openDialog, closeDialog } = useDialogSearchParam();
  const createModalOpen = dialog?.type === "ticket";

  const { offset } = usePageOffset();

  const { filters, setFilter, clearFilters } = useFilterSearchParams({
    keys: ["search", "status", "priority", "type", "owner"],
    defaults: { status: "All", priority: "All", type: "All", owner: "All" },
  });
  const searchQuery = filters.search;
  const statusFilter = filters.status as StatusFilter;
  const priorityFilter = filters.priority as PriorityFilter;
  const typeFilter = filters.type as TypeFilter;
  const ownerFilter = filters.owner as OwnerFilter;

  // FilterToolbar already debounces draft → URL commits at 300ms, so
  // searchQuery here is the committed (debounced) value.
  const q = useMemo(() => {
    const trimmed = searchQuery.trim();
    return trimmed.length >= 3 ? trimmed : undefined;
  }, [searchQuery]);

  const {
    data,
    isLoading: loading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      ...queryKeys.tickets({ limit: DEFAULT_PAGE_SIZE, offset }),
      q,
      statusFilter,
      priorityFilter,
      typeFilter,
      ownerFilter,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("limit", DEFAULT_PAGE_SIZE.toString());
      params.set("offset", offset.toString());
      if (q) params.set("q", q);
      if (statusFilter !== "All") params.append("status", statusFilter);
      if (priorityFilter !== "All") params.append("priority", priorityFilter);
      if (typeFilter !== "All") params.append("type", typeFilter);
      if (ownerFilter === "Mine") params.set("scope", "mine");
      else if (ownerFilter === "Unassigned") params.set("scope", "unassigned");
      return apiClient.get<PaginatedResponse<Ticket>>(
        `/tickets?${params.toString()}`,
      );
    },
  });
  const tickets = useMemo(() => data?.data ?? [], [data?.data]);
  const total = data?.total ?? 0;
  const { page, totalPages, setPage } = usePagination({ total });

  const currentUser = useMemo(() => getCurrentUser(), []);

  // ─── Resizable columns ──────────────────────────────────────────────
  const columnDefs = useMemo((): ColumnDef[] => {
    const allCols: ColumnDef[] = [
      { key: "item", defaultWidth: 160, minWidth: 100 },
      { key: "sla", defaultWidth: 120, minWidth: 80 },
      { key: "reason", defaultWidth: 140, minWidth: 90 },
      { key: "status", defaultWidth: 160, minWidth: 100 },
      { key: "owner", defaultWidth: 140, minWidth: 80 },
      { key: "action", defaultWidth: 120, minWidth: 80 },
    ];
    if (isRegular)
      return allCols.filter((c) => !["sla", "owner"].includes(c.key));
    return allCols;
  }, [isRegular]);

  const handleTicketClick = (ticketId: string) => {
    void navigate({ to: `/tickets/${ticketId}` });
  };

  const handleCreateTicket = async (formData: TicketFormData) => {
    if (!entity) return;
    await apiClient.post<Ticket>("/tickets", {
      entity_type: entity.type,
      entity_id: entity.id,
      title: formData.title,
      description: formData.description || undefined,
      priority: formData.priority,
    });
    void refetch();
    closeDialog();
  };

  // Page reset on filter change is handled by useFilterSearchParams.
  // Filters apply server-side; the raw `tickets` array is what gets rendered.

  const {
    sort,
    toggleSort,
    sortedData: sortedTickets,
  } = useSortableTable(tickets, { defaultColumn: "priority" });

  const hasActiveFilters =
    statusFilter !== "All" ||
    priorityFilter !== "All" ||
    typeFilter !== "All" ||
    ownerFilter !== "All";

  const activeFilterCount = [priorityFilter, typeFilter, ownerFilter].filter(
    (f) => f !== "All",
  ).length;

  const statusSelectOptions = [
    { value: "All", label: tTickets("allStatuses") },
    ...TICKET_STATUSES.map((s) => ({
      value: s,
      label: getTicketStatusLabel(s, tCommon),
    })),
  ];
  const prioritySelectOptions = [
    { value: "All", label: tTickets("allPriorities") },
    ...TICKET_PRIORITIES.map((p) => ({
      value: p,
      label: getTicketPriorityLabel(p, tTickets),
    })),
  ];
  const typeSelectOptions = [
    { value: "All", label: tTickets("allTypes") },
    ...TICKET_TYPES.map((tt) => ({
      value: tt,
      label: getTicketTypeLabel(tt, tTicketTypes),
    })),
  ];
  const ownerSelectOptions = [
    { value: "All", label: tTickets("allOwners") },
    { value: "Mine", label: tTickets("ownerMine") },
    { value: "Unassigned", label: tTickets("ownerUnassigned") },
  ];

  return (
    <VStack gap="lg" data-testid="tickets-page">
      <PageTitle>{tTickets("title")}</PageTitle>
      {entity?.type && (
        <ActionQueueCards
          role={entity.type}
          mode="full"
          activeFilter={typeFilter !== "All" ? typeFilter : undefined}
          onFilterByType={(ticketType) => {
            setFilter("type", ticketType === typeFilter ? "All" : ticketType);
          }}
          data-testid="tickets-action-queue"
        />
      )}

      <Card
        variant="bordered"
        padding="xs"
        data-testid="tickets-filters-card"
        className="sticky top-16 z-sticky"
      >
        <FilterToolbar
          searchPlaceholder={tTickets("searchPlaceholder")}
          searchValue={searchQuery}
          onSearchChange={(v) => setFilter("search", v)}
          pagination={{
            page,
            totalPages,
            perPage: DEFAULT_PAGE_SIZE,
            onPageChange: setPage,
            loading,
          }}
          quickFilters={
            <>
              {statusSelectOptions.map((opt) => (
                <FilterChip
                  key={opt.value}
                  active={statusFilter === opt.value}
                  onClick={() => setFilter("status", opt.value)}
                  data-testid={`tickets-filter-status-${opt.value}`}
                >
                  {opt.label}
                </FilterChip>
              ))}
            </>
          }
          advancedFilters={[
            <CustomSelect
              key="priority-filter"
              value={priorityFilter}
              onChange={(value) => setFilter("priority", value)}
              data-testid="tickets-filter-priority"
              options={prioritySelectOptions}
              label={tTickets("priority")}
            />,
            <CustomSelect
              key="type-filter"
              value={typeFilter}
              onChange={(value) => setFilter("type", value)}
              data-testid="tickets-filter-type"
              options={typeSelectOptions}
              label={tTickets("type")}
            />,
            <CustomSelect
              key="owner-filter"
              value={ownerFilter}
              onChange={(value) => setFilter("owner", value)}
              data-testid="tickets-filter-owner"
              options={ownerSelectOptions}
              label={tTickets("owner")}
            />,
          ]}
          activeFilterCount={activeFilterCount}
          onClearFilters={clearFilters}
          createAction={{
            label: tTickets("createTicket"),
            onClick: () => openDialog("ticket"),
            testId: "tickets-create-button",
          }}
          data-testid="tickets-filters"
        />
      </Card>

      <Card padding="none" data-testid="tickets-table-card">
        <TableViewport position="relative">
          {loading && tickets.length === 0 ? (
            <TableSkeleton columns={isCompact ? 2 : 6} rows={5} />
          ) : isError ? (
            <ErrorState
              data-testid="tickets-error"
              title={tCommon("errorLoadingData")}
              action={
                <Button
                  color="neutral"
                  variant="outline"
                  onClick={() => void refetch()}
                >
                  {tCommon("tryAgain")}
                </Button>
              }
              className="py-16"
            />
          ) : sortedTickets.length === 0 ? (
            <EmptyState
              title={tTickets("noMatchingTickets")}
              description={
                hasActiveFilters || searchQuery.trim()
                  ? tNotificationsEmpty("noResultsDescription")
                  : undefined
              }
              action={
                hasActiveFilters ? (
                  <Button
                    color="neutral"
                    variant="outline"
                    onClick={clearFilters}
                  >
                    {tCommon("clear")}
                  </Button>
                ) : undefined
              }
              className="py-16"
            />
          ) : isCompact ? (
            /* ── Compact: card list with visible_fields ── */
            <DividedList>
              {sortedTickets.map((ticket) => {
                const fields = ticketSearchableFields(ticket, language);
                return (
                  <InteractiveCard
                    key={ticket.id}
                    onClick={() => handleTicketClick(ticket.id)}
                    data-testid={`tickets-card-${ticket.id}`}
                  >
                    <HStack gap="sm">
                      <Text
                        size="sm"
                        weight={ticket.unread ? "semibold" : "medium"}
                      >
                        {highlight(fields.id, searchQuery)}
                      </Text>
                      {ticket.unread && <StatusDot color="primary" />}
                      <Text className="ml-auto">
                        <SLABadge dueDate={ticket.due_date} />
                      </Text>
                    </HStack>
                    <Text>{highlight(fields.title, searchQuery)}</Text>
                    <HStack wrap align="stretch" gap="sm">
                      <Badge color={ticketStatusColor[ticket.status]}>
                        {highlight(fields.status, searchQuery)}
                      </Badge>
                      <Badge color={ticketPriorityColor[ticket.priority]}>
                        {highlight(fields.priority, searchQuery)}
                      </Badge>
                      <ReasonBadge ticketType={ticket.type as TicketType} />
                      {searchQuery && fields.type.value ? (
                        <Text size="xs" color="muted">
                          {highlight(fields.type, searchQuery)}
                        </Text>
                      ) : null}
                    </HStack>
                  </InteractiveCard>
                );
              })}
            </DividedList>
          ) : (
            /* ── Regular / Expanded: resizable table ── */
            <ResizableTableShell
              tableId="tickets"
              userId={currentUser?.id ?? null}
              columns={columnDefs}
            >
              {({ onResizeStart }) => (
                <>
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell
                        column="created_at"
                        sortColumn={sort?.column}
                        sortDirection={sort?.direction}
                        onSort={toggleSort}
                        className="relative"
                      >
                        {tTickets("columns.item")}
                        <ResizeHandle
                          columnKey="item"
                          onResizeStart={onResizeStart}
                        />
                      </SortableHeaderCell>
                      {!isRegular && (
                        <SortableHeaderCell
                          column="priority"
                          sortColumn={sort?.column}
                          sortDirection={sort?.direction}
                          onSort={toggleSort}
                          className="relative"
                        >
                          {tTickets("columns.sla")}
                          <ResizeHandle
                            columnKey="sla"
                            onResizeStart={onResizeStart}
                          />
                        </SortableHeaderCell>
                      )}
                      <TableHeaderCell className="relative">
                        {tTickets("columns.reason")}
                        <ResizeHandle
                          columnKey="reason"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <SortableHeaderCell
                        column="status"
                        sortColumn={sort?.column}
                        sortDirection={sort?.direction}
                        onSort={toggleSort}
                        className="relative"
                      >
                        {tTickets("columns.status")}
                        <ResizeHandle
                          columnKey="status"
                          onResizeStart={onResizeStart}
                        />
                      </SortableHeaderCell>
                      {!isRegular && (
                        <TableHeaderCell className="relative">
                          {tTickets("columns.owner")}
                          <ResizeHandle
                            columnKey="owner"
                            onResizeStart={onResizeStart}
                          />
                        </TableHeaderCell>
                      )}
                      <TableHeaderCell className="relative text-right" sticky>
                        {tTickets("columns.action")}
                        <ResizeHandle
                          columnKey="action"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell className="w-auto" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedTickets.map((ticket) => {
                      const fields = ticketSearchableFields(ticket, language);
                      return (
                        <TableRow key={ticket.id}>
                          <TableCell>
                            <HStack gap="sm">
                              <Text
                                size="sm"
                                weight={ticket.unread ? "semibold" : "medium"}
                              >
                                {highlight(fields.id, searchQuery)}
                              </Text>
                              {ticket.unread ? (
                                <StatusDot color="primary" />
                              ) : null}
                            </HStack>
                            <Text size="xs" color="muted">
                              {formatDate(ticket.created_at, language)}
                            </Text>
                          </TableCell>
                          {!isRegular && (
                            <TableCell>
                              <SLABadge dueDate={ticket.due_date} />
                            </TableCell>
                          )}
                          <TableCell>
                            <HStack align="stretch" gap="sm">
                              <ReasonBadge
                                ticketType={ticket.type as TicketType}
                              />
                              {searchQuery && fields.type.value ? (
                                <Text size="xs" color="muted">
                                  {highlight(fields.type, searchQuery)}
                                </Text>
                              ) : null}
                            </HStack>
                          </TableCell>
                          <TableCell>
                            <HStack align="stretch" gap="sm">
                              <Badge color={ticketStatusColor[ticket.status]}>
                                {highlight(fields.status, searchQuery)}
                              </Badge>
                              <Badge
                                color={ticketPriorityColor[ticket.priority]}
                              >
                                {highlight(fields.priority, searchQuery)}
                              </Badge>
                            </HStack>
                          </TableCell>
                          {!isRegular && (
                            <TableCell>
                              <Text color="muted">
                                {ticket.assignee
                                  ? highlight(fields.assignee_name, searchQuery)
                                  : tTickets("unassigned")}
                              </Text>
                            </TableCell>
                          )}
                          <TableCell className="text-right" sticky>
                            <Button
                              type="button"
                              color="neutral"
                              variant="outline"
                              size="sm"
                              onClick={() => handleTicketClick(ticket.id)}
                              data-testid={`tickets-list-item-${ticket.id}-view`}
                            >
                              {tCommon("view")}
                            </Button>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </>
              )}
            </ResizableTableShell>
          )}
          {loading && tickets.length > 0 && (
            <PageLoadingOverlay
              page={page}
              label={tCommon("pagination.loadingPage")}
            />
          )}
        </TableViewport>
      </Card>

      {/* ── FAB: compact create action ── */}
      {isCompact && (
        <Fab
          onClick={() => openDialog("ticket")}
          aria-label={tTickets("createTicket")}
          data-testid="tickets-create-fab"
        />
      )}

      {/* ── Create modal: AdaptiveSheet on compact, standard modal otherwise ── */}
      {isCompact ? (
        <AdaptiveSheet
          open={createModalOpen}
          onDismiss={() => closeDialog()}
          detents={["large"]}
          size="lg"
        >
          <TicketCreateModal
            isOpen={createModalOpen}
            onClose={() => closeDialog()}
            onSubmit={handleCreateTicket}
            naked
            data-testid="ticket-create-modal"
          />
        </AdaptiveSheet>
      ) : (
        <TicketCreateModal
          isOpen={createModalOpen}
          onClose={() => closeDialog()}
          onSubmit={handleCreateTicket}
          data-testid="ticket-create-modal"
        />
      )}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/tickets/")({
  component: TicketsPage,
});
